import { useState, useEffect, useRef, Fragment } from 'react';
import axios from 'axios';
import '../styles/proposalPrint.css';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useConfirm } from 'material-ui-confirm';
import { useReactToPrint } from 'react-to-print';
import html2pdf from 'html2pdf.js';

import { CssBaseline, Box, Typography, Paper, Radio, Button as MuiButton, Grid } from '@mui/material';

// Custom Components
import {
  Spinner,
  BrowserTitle,
  ProposalViewHeader,
  ProposalViewEditSection,
  ProposalViewAwards,
  ProposalViewContacts,
  ProposalViewSystemEquipment,
  ProposalViewSystemWorks,
  ProposalViewSystemWarranties,
  ProposalViewAdditionalInfo,
  ProposalViewJobOverview,
  ProposalViewEquipmentOverview,
  ProposalViewAirflow,
  ProposalViewWorkDescription,
  ProposalViewInvestment,
  ProposalViewGuarantees,
  ProposalViewFooter,
  ProposalViewSignature,
  EquipmentImage,
} from '../components';

// Utils
import messages from '../static/messages';
import { calculateTotalPay, calculateTotalPayForOption, calculateTotalRebates, calculateTotalInstants } from '../utils/proposalCalculations';

// Services
import publicServices from '../services/publicServices';
import { API_URL } from '../api/axios';

// Font family constant
const fontFamily = '"Inter", sans-serif';

export const container = {
  maxWidth: 950,
  margin: '0 auto',
};

const ProposalPublicView = () => {
  const [proposal, setProposal] = useState();
  const [pdfLoading, setPdfLoading] = useState(false);
  const [equipmentTypes, setEquipmentTypes] = useState({});
  const [selectedOptionId, setSelectedOptionId] = useState(null);
  const [smallLogo, setSmallLogo] = useState(false);
  const [notFound, setNotFound] = useState(false);

  const { token } = useParams();
  const confirm = useConfirm();
  const printRef = useRef();

  const handlePrint = useReactToPrint({
    content: () => printRef.current,
    documentTitle: `proposal_${proposal?.publicId || 'document'}`,
    onAfterPrint: () => toast.info('Document printed'),
  });

  const handleDownloadPdf = () => {
    if (!printRef.current) return;
    setPdfLoading(true);
    
    const element = printRef.current;
    
    // Hide no-print elements (edit buttons, etc.) — html2canvas ignores @media print
    const noPrintEls = element.querySelectorAll('.no-print');
    noPrintEls.forEach(el => el.style.display = 'none');
    
    const opt = {
      margin: [10, 10, 10, 10],
      filename: `Proposal-${proposal?.publicId || 'document'}.pdf`,
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true, letterRendering: true },
      jsPDF: { unit: 'mm', format: 'a4', orientation: 'portrait' },
      pagebreak: { mode: ['css', 'legacy'] }
    };
    
    html2pdf()
      .set(opt)
      .from(element)
      .save()
      .then(() => {
        setPdfLoading(false);
        noPrintEls.forEach(el => el.style.display = '');
        toast.info('PDF downloaded');
      })
      .catch((err) => {
        setPdfLoading(false);
        noPrintEls.forEach(el => el.style.display = '');
        console.error(err);
        toast.error('Failed to generate PDF');
      });
  };

  const getProposal = async () => {
    try {
      const response = await publicServices.getProposalByPublicToken(token);
      if (response.status === 200) {
        setProposal(response.data);
      }
    } catch (error) {
      console.error(error);
      if (error.response?.status === 404) {
        setNotFound(true);
      } else {
        toast.error(messages.errors.error_data_loaning);
      }
    }
  };

  const getEquipmentTypes = async () => {
    try {
      // Use raw axios (no auth interceptor) so public view doesn't redirect to login on 401
      const response = await axios.get(`${API_URL}/equipment-types`);
      if (response?.data?.data) {
        const typesMap = {};
        response.data.data.forEach(type => {
          typesMap[type.id] = type;
        });
        setEquipmentTypes(typesMap);
      }
    } catch (error) {
      // Silently fail — equipment types are optional (only for images in Installation proposals)
      console.log('Equipment types load error (public view):', error);
    }
  };

  const handleSign = (signData) => {
    confirm({
      title: 'Confirm Signature',
      description: 'By signing, you agree to the terms and conditions of this proposal.',
    })
      .then(() => signProposal(signData))
      .catch(() => toast.info('Signature cancelled'));
  };

  const signProposal = async (signData) => {
    // For public view, signing is disabled - client needs to use the token-based view
    toast.info('Please use the link sent to your email to sign this proposal');
  };

  useEffect(() => {
    if (token) {
      getProposal();
      getEquipmentTypes();
    }
  }, [token]);

  const proposalOptions = proposal?.proposalOptions || [];
  const hasActiveOptions = proposalOptions.length > 0 && !proposal?.optionAccepted;

  const effectiveSelectedOptionId = selectedOptionId
    || (hasActiveOptions && proposalOptions.length > 0 ? proposalOptions[0].id : null);

  useEffect(() => {
    if (hasActiveOptions && !selectedOptionId && proposalOptions.length > 0) {
      setSelectedOptionId(proposalOptions[0].id);
    }
  }, [hasActiveOptions, selectedOptionId, proposalOptions]);

  if (notFound) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '100vh',
        backgroundColor: '#f5f5f5'
      }}>
        <Typography variant="h4" sx={{ color: '#666', mb: 2 }}>
          Proposal Not Found
        </Typography>
        <Typography variant="body1" sx={{ color: '#999' }}>
          This link may have expired or the proposal no longer exists.
        </Typography>
      </Box>
    );
  }

  if (!proposal) return <Spinner />;

  const allSystems = proposal?.proposalSystems || [];
  const isServiceProposal = proposal?.proposalType === 'Service';

  const activeOptionId = hasActiveOptions
    ? effectiveSelectedOptionId
    : (proposal.optionAccepted ? proposal.selectedOptionId : null);
  const publicTotalPay = activeOptionId
    ? calculateTotalPayForOption(proposal, activeOptionId)
    : calculateTotalPay(proposal);

  // Filter systems for display when option accepted
  const systems = proposal.optionAccepted && proposal.selectedOptionId
    ? allSystems.filter(s => s.optionId === proposal.selectedOptionId)
    : allSystems;

  const handleAcceptOption = async (optionId) => {
    try {
      const response = await publicServices.acceptOptionByToken(token, optionId);
      if (response.status === 200) {
        toast.success('Option accepted!');
        getProposal();
      }
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to accept option');
    }
  };

  return (
    <Fragment>
      <CssBaseline />
      <BrowserTitle title={`Proposal ${proposal?.publicId || ''} | Airmaxx Pro`} />

      <Box ref={printRef} sx={{ 
        p: 4, 
        backgroundColor: '#fff',
        fontFamily,
        '@media print': {
          p: 0,
        }
      }}>
        <Box sx={container}>
          {/* Header */}
          <ProposalViewHeader 
            proposal={proposal}
            handlePdf={handleDownloadPdf}
            handlePrint={handlePrint}
            pdfLoading={pdfLoading}
            token={true}
            totalPay={publicTotalPay}
            isAccepted={!!proposal.signature}
            handleSignatureModalOpen={() => {}}
          />

          {/* Edit Section (hide for public) */}
          <ProposalViewEditSection id={proposal.id} client={true} smallLogo={smallLogo} setSmallLogo={setSmallLogo} />

          {/* Awards */}
          <ProposalViewAwards
            techName={proposal.createdBy?.fullName}
            techPhone={proposal.createdBy?.phoneNumber}
            proposalId={proposal.publicId}
            issueDate={proposal.createdAt}
            installationSchedule={proposal.installationSchedule || []}
            smallLogo={smallLogo}
          />

          {/* Contacts */}
          <ProposalViewContacts property={proposal.property} selectedClientId={proposal.selectedClientId} billingAddress={proposal.billingAddress} />

          {/* Job Overview */}
          {proposal.jobOverview && <ProposalViewJobOverview description={proposal.jobOverview} />}

          {/* Equipment Overview */}
          {proposal.equipmentOverview && <ProposalViewEquipmentOverview description={proposal.equipmentOverview} />}

          {/* Work Description (Service) */}
          {isServiceProposal && proposal.workDescriptions?.length > 0 && (
            <ProposalViewWorkDescription items={proposal.workDescriptions} />
          )}

          {/* Option Columns (when options exist and not accepted) */}
          {!isServiceProposal && hasActiveOptions && proposalOptions.length > 0 && (
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'inline-block', mb: 2 }}>
                <Box sx={{ backgroundColor: '#0d3c61', color: '#fff', px: 2, py: 0.75 }}>
                  <Typography sx={{ fontFamily, fontWeight: 600, fontSize: 14, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
                    Equipment
                  </Typography>
                </Box>
              </Box>
              <Grid container spacing={2}>
                {[...proposalOptions].sort((a, b) => a.orderIndex - b.orderIndex).map((option) => {
                  const optSystems = allSystems.filter(s => s.optionId === option.id && s.equipments?.length > 0);
                  const isSelected = effectiveSelectedOptionId === option.id;
                  return (
                    <Grid item xs={12} sm={12 / proposalOptions.length} key={option.id}>
                      <Box sx={{
                        border: isSelected ? '2px solid #0d3c61' : '1px solid #e0e0e0',
                        borderRadius: 2, p: 2,
                        backgroundColor: isSelected ? '#f0f7ff' : '#fafafa',
                        transition: 'all 0.2s ease', height: '100%',
                        display: 'flex', flexDirection: 'column',
                      }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, cursor: 'pointer' }} onClick={() => setSelectedOptionId(option.id)}>
                          <Radio checked={isSelected} onChange={() => setSelectedOptionId(option.id)} sx={{ p: 0.5, mr: 1 }} />
                          <Typography sx={{ fontFamily, fontWeight: 700, fontSize: 13, textTransform: 'uppercase', letterSpacing: '0.5px', color: '#0d3c61' }}>
                            {option.name}
                          </Typography>
                        </Box>
                        <Box sx={{ flex: 1 }}>
                          {optSystems.map((system) => (
                            <Box key={system.id} sx={{ mb: 1.5 }}>
                              {optSystems.length > 1 && (
                                <Typography sx={{ fontFamily, fontSize: 11, fontWeight: 600, color: '#999', textTransform: 'uppercase', mb: 0.5 }}>
                                  {system.name}
                                </Typography>
                              )}
                              {[...(system.equipments || [])].sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0)).map((eq) => (
                                <Typography key={eq.id} sx={{ fontFamily, fontSize: 13, color: '#333', py: 0.3, borderBottom: '1px solid #f0f0f0' }}>
                                  {eq.manufacturerName && <span style={{ fontWeight: 700 }}>{eq.manufacturerName}</span>}{eq.manufacturerName ? ' ' : ''}{eq.modelNumber}
                                </Typography>
                              ))}
                            </Box>
                          ))}
                        </Box>
                        <MuiButton
                          variant={isSelected ? "contained" : "outlined"}
                          fullWidth
                          onClick={() => handleAcceptOption(option.id)}
                          sx={{
                            mt: 2, fontFamily, fontWeight: 600, fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px',
                            backgroundColor: isSelected ? '#0d3c61' : 'transparent',
                            borderColor: '#0d3c61', color: isSelected ? '#fff' : '#0d3c61',
                            '&:hover': { backgroundColor: isSelected ? '#0a2d4a' : 'rgba(13,60,97,0.08)' },
                          }}
                        >
                          Accept This Option
                        </MuiButton>
                        <Typography
                          sx={{
                            fontFamily,
                            fontWeight: 700,
                            fontSize: 15,
                            color: '#4caf50',
                            textAlign: 'center',
                            mt: 1.5,
                          }}
                        >
                          {calculateTotalPayForOption(proposal, option.id).toLocaleString('en-US', { style: 'currency', currency: 'USD' })}
                        </Typography>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          )}

          {/* Systems (Installation) — standard view */}
          {!isServiceProposal && !hasActiveOptions && systems.map((system, index) => (
            <Paper key={system.id || index} elevation={0} sx={{ mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#0d3c61', borderBottom: '2px solid #0d3c61', pb: 1 }}>
                System {index + 1}: {system.name || 'HVAC System'}
              </Typography>

              {system.equipments?.length > 0 && (
                <EquipmentImage 
                  equipments={system.equipments} 
                  equipmentTypes={equipmentTypes}
                />
              )}

              <ProposalViewSystemEquipment arr={system.equipments || []} handleChange={() => {}} system_index={index} />

              {system.scopeOfWorks?.length > 0 && (
                <ProposalViewSystemWorks arr={system.scopeOfWorks} handleChange={() => {}} system_index={index} />
              )}

              {system.warranties?.length > 0 && (
                <ProposalViewSystemWarranties arr={system.warranties} handleChange={() => {}} system_index={index} />
              )}
            </Paper>
          ))}

          {/* Airflow */}
          {proposal.proposalAirflows?.length > 0 && (
            <ProposalViewAirflow airflows={proposal.proposalAirflows} />
          )}

          {/* Guarantees */}
          <ProposalViewGuarantees guarantees={proposal?.guarantees} />

          {/* Additional Info */}
          {proposal.informationForCustomer?.length > 0 && (
            <ProposalViewAdditionalInfo info={proposal.informationForCustomer} />
          )}

          {/* Investment */}
          <ProposalViewInvestment 
            totalPay={publicTotalPay}
            rebates={calculateTotalRebates(proposal)}
            instants={calculateTotalInstants(proposal)}
            discountsList={proposal?.discounts || []}
            rebatesList={proposal?.contractRebates || []}
            financingOptions={proposal?.financingOptions}
            isSigned={!!(proposal?.signatureId || proposal?.signature)}
          />

          {/* Signature */}
          <ProposalViewSignature 
            signature={proposal.signature}
            onSign={handleSign}
            token={true}
            isAccepted={!!proposal.signature}
            customerName={((proposal.selectedClientId && proposal.property?.clients?.find((c) => c.client?.id === proposal.selectedClientId)?.client) || proposal.property?.clients?.[0]?.client)?.fullName}
          />

          {/* Footer */}
          <ProposalViewFooter 
            techPhone={proposal.createdBy?.phoneNumber}
            techEmail={proposal.createdBy?.email}
          />
        </Box>
      </Box>
    </Fragment>
  );
};

export default ProposalPublicView;

